import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Avatar } from "../../components/Avatar.js";
import { IconDown, IconUp } from "../../components/icons.js";
import { Button, IconButton, Notice } from "../../components/ui.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import { drawOrder, moveSeed, seedNext, seededList, unseed } from "../../lib/event-seeds.js";
import { fetchRatings, setEventSeeds } from "../../lib/tournaments.js";
import type { TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen, useDraw } from "./shared.js";

/**
 * The order an event is drawn in, for an organiser to set.
 *
 * Seeded entries are drawn first, in seed order; everyone else follows by rating, as the draw
 * always did. So the list here is the next draw's order exactly — the same `seedEntrants` the
 * draw runs — and seeding nobody leaves it as it was.
 *
 * Each tap saves the whole seeded list, which is what lets "move up" be a single write. Once a
 * result is in the seeds are fixed: they decided a draw that is now being played. A team tie
 * seeds its team sheets and an auction pool its panel, so neither has this page.
 */
export function EventSeeding() {
  return <EventGate>{({ event }) => <Seeds event={event} />}</EventGate>;
}

/** A team tie seeds its team sheets and an auction pool its panel; every other format seeds here. */
export const offersSeeding = (event: TournamentEvent) =>
  event.format !== "team_tie" && event.format !== "auction_pool";

/** "2 seeded", or nothing when the draw is by rating alone. */
export function seedingHint(event: TournamentEvent): string {
  const count = seededList(event.entries).length;
  return count ? `${count} seeded` : "";
}

function Seeds({ event }: { event: TournamentEvent }) {
  const { member } = useAuth();
  const queryClient = useQueryClient();
  const organiser = hasRole(member, "organiser");

  const registered = event.entries.filter((entry) => entry.status === "registered");
  const { data: ratings = new Map<string, number>() } = useQuery({
    queryKey: ["ratings", event.kind, registered.map((entry) => entry.memberId).sort()],
    queryFn: () =>
      fetchRatings(
        registered.map((entry) => entry.memberId),
        event.kind,
      ),
    enabled: organiser,
  });
  const { data: fixtures = [] } = useDraw(event.id);

  // What the last tap asked for, until the refetched event says the same. Without it a
  // second tap on a slow connection would start from the list before the first.
  const [pending, setPending] = useState<string[] | null>(null);
  const seeded = pending ?? seededList(event.entries);

  const save = useMutation({
    mutationFn: (next: string[]) => setEventSeeds(event.id, next),
    onMutate: (next) => setPending(next),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      setPending(null);
    },
  });

  if (!organiser) return <Navigate to={`/events/${event.id}`} replace />;
  if (!offersSeeding(event)) return <Navigate to={`/events/${event.id}/manage`} replace />;

  const locked = fixtures.some((fixture) => fixture.result);
  const drawn = fixtures.length > 0;
  const order = drawOrder(event.entries, seeded, ratings);
  const byId = new Map(registered.map((entry) => [entry.memberId, entry]));
  // A withdrawal left a hole (1, 3). The next save closes it, so "Seed 3" would be a number
  // somebody else is about to stop holding; just "Seed" until then.
  const gapped = !pending && seeded.some((id, at) => byId.get(id)?.seed !== at + 1);

  return (
    <EventScreen event={event} title="Seeding">
      <p className="mb-2 text-sm text-ink-muted">
        Seeded players are drawn first, in seed order. Everyone else follows by rating, unrated
        last. Seed as few or as many as you like.
      </p>
      {locked ? (
        <Notice tone="attention">This draw already has results, so its seeds are fixed.</Notice>
      ) : drawn ? (
        <p className="micro mb-3">
          A draw exists. Changing seeds does not change it — draw again to use them.
        </p>
      ) : null}

      {order.length === 0 ? (
        <p className="micro">Nobody is registered yet.</p>
      ) : (
        <ol data-testid="seeding-order">
          {order.map((memberId, at) => {
            const entry = byId.get(memberId);
            const name = entry?.displayName ?? "Someone";
            const place = seeded.indexOf(memberId) + 1;
            const isSeeded = place > 0;
            // The stored number, gap and all, so this agrees with the entry list; a save closes
            // the gap. While a save is in flight the list is ahead of the event, so its place.
            const seed = pending ? place : (entry?.seed ?? place);
            return (
              <li
                key={memberId}
                className="flex items-center gap-3 border-b"
                style={{ borderColor: "var(--color-line-muted)", minHeight: 56 }}
              >
                <span
                  className="font-mono w-8 shrink-0 text-right text-sm font-semibold"
                  aria-label={isSeeded ? `Seed ${seed}` : `Position ${at + 1}, unseeded`}
                >
                  {isSeeded ? seed : <span className="text-ink-muted">·</span>}
                </span>
                <Avatar name={name} url={entry?.avatarUrl ?? null} size={26} />
                <span className="min-w-0 flex-1 truncate text-base font-semibold">{name}</span>
                {locked ? null : isSeeded ? (
                  <span className="flex shrink-0 items-center">
                    <IconButton
                      label={`Move ${name} up`}
                      disabled={place === 1 || save.isPending}
                      onClick={() => save.mutate(moveSeed(seeded, memberId, -1))}
                    >
                      <IconUp />
                    </IconButton>
                    <IconButton
                      label={`Move ${name} down`}
                      disabled={place === seeded.length || save.isPending}
                      onClick={() => save.mutate(moveSeed(seeded, memberId, 1))}
                    >
                      <IconDown />
                    </IconButton>
                    <Button
                      tone="quiet"
                      disabled={save.isPending}
                      onClick={() => save.mutate(unseed(seeded, memberId))}
                      style={{ minHeight: 36, fontSize: 13 }}
                    >
                      Unseed
                    </Button>
                  </span>
                ) : (
                  <Button
                    tone="quiet"
                    disabled={save.isPending}
                    onClick={() => save.mutate(seedNext(seeded, memberId))}
                    style={{ minHeight: 36, fontSize: 13 }}
                  >
                    {gapped ? "Seed" : `Seed ${seeded.length + 1}`}
                  </Button>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {save.isError ? <Notice tone="attention">{(save.error as Error).message}</Notice> : null}
    </EventScreen>
  );
}
